import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Heart, Search, Plus, MessageCircle, ThumbsUp, Send, Tag } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import api from '../api/api';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import SEOMeta from '../components/SEOMeta';

// Renderer markdown leggero per gli articoli community
function renderInline(text) {
  // **bold** → <strong>
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) =>
    part.startsWith('**') && part.endsWith('**')
      ? <strong key={i} className="font-semibold text-[var(--color-deep)]">{part.slice(2, -2)}</strong>
      : part
  );
}

function PostContent({ content }) {
  const paragraphs = content.split(/\n\n+/);
  return (
    <div className="space-y-3 text-sm text-[var(--color-muted-text)] leading-relaxed">
      {paragraphs.map((para, pi) => {
        const lines = para.split('\n').filter(Boolean);
        // Tabella markdown (| col |)
        if (lines[0]?.startsWith('|')) {
          const rows = lines.filter(l => l.startsWith('|') && !l.match(/^\|[-| ]+\|$/));
          return (
            <div key={pi} className="overflow-x-auto my-2">
              <table className="w-full text-xs border-collapse">
                {rows.map((row, ri) => {
                  const cells = row.split('|').filter(c => c.trim());
                  return (
                    <tr key={ri} className={ri === 0 ? 'bg-[var(--color-sage-soft)] font-semibold' : 'border-t border-[var(--color-border)]'}>
                      {cells.map((cell, ci) => <td key={ci} className="px-3 py-1.5">{renderInline(cell.trim())}</td>)}
                    </tr>
                  );
                })}
              </table>
            </div>
          );
        }
        // Liste con frecce/emoji/numeri
        const isList = lines.every(l => /^(→|✅|❌|\d+\.|[-•])/.test(l.trimStart()));
        if (isList && lines.length > 1) {
          return (
            <ul key={pi} className="space-y-1.5 pl-1">
              {lines.map((line, li) => {
                const clean = line.replace(/^(→|✅|❌|\d+\.|[-•])\s*/, '');
                const icon = line.match(/^→/) ? '→' : line.match(/^✅/) ? '✅' : line.match(/^❌/) ? '❌' : line.match(/^\d+\./) ? line.match(/^\d+\./)[0] : '•';
                const isGreen = icon === '✅';
                const isRed = icon === '❌';
                return (
                  <li key={li} className="flex gap-2">
                    <span className={`shrink-0 font-medium ${isGreen ? 'text-green-600' : isRed ? 'text-red-500' : 'text-[var(--color-sage)]'}`}>{icon}</span>
                    <span>{renderInline(clean)}</span>
                  </li>
                );
              })}
            </ul>
          );
        }
        // Paragrafo normale (tutte le righe insieme)
        const fullText = lines.join(' ');
        return <p key={pi}>{renderInline(fullText)}</p>;
      })}
    </div>
  );
}

export default function CommunityPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [newPostDialog, setNewPostDialog] = useState(false);
  const [postForm, setPostForm] = useState({ title: '', content: '', tags: '', photo_url: '' });
  const [commentForms, setCommentForms] = useState({});
  const [expandedPost, setExpandedPost] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const popularTags = ['cremazione', 'trasportabilità', 'urne', 'lutto', 'dispersione', 'volo', 'normativa', 'memorial', 'bambini', 'supporto', 'giardino', 'ceneri'];

  useEffect(() => { fetchPosts(); }, [page]);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: '8' });
      if (search) params.append('search', search);
      const r = await api.get(`/community/posts?${params}`);
      setPosts(r.data.items || []);
      setTotal(r.data.total || 0);
      setTotalPages(r.data.pages || 1);
    } catch {} finally { setLoading(false); }
  };

  const handleSearch = (e) => { e.preventDefault(); setPage(1); fetchPosts(); };

  const handleLike = async (postId) => {
    try {
      const r = await api.post(`/community/posts/${postId}/like`);
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, likes: r.data.likes } : p));
    } catch { toast.error(t('common.error')); }
  };

  const handleComment = async (postId) => {
    const content = commentForms[postId] || '';
    if (!content.trim()) return;
    try {
      const r = await api.post(`/community/posts/${postId}/comments`, { content, author_name: user?.first_name || 'Anonimo' });
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, comments: [...(p.comments || []), r.data] } : p));
      setCommentForms(prev => ({ ...prev, [postId]: '' }));
      toast.success('Commento pubblicato!');
    } catch (err) { toast.error(err.response?.data?.detail || t('common.error')); }
  };

  const handleCreatePost = async (e) => {
    e.preventDefault();
    if (!postForm.title.trim() || !postForm.content.trim()) { toast.error('Titolo e contenuto obbligatori'); return; }
    setSubmitting(true);
    try {
      const tags = postForm.tags ? postForm.tags.split(',').map(t => t.trim()).filter(Boolean) : [];
      await api.post('/community/posts', { title: postForm.title, content: postForm.content, tags, photo_url: postForm.photo_url || null });
      toast.success('Post pubblicato!');
      setNewPostDialog(false);
      setPostForm({ title: '', content: '', tags: '', photo_url: '' });
      setPage(1);
      fetchPosts();
    } catch (err) { toast.error(err.response?.data?.detail || t('common.error')); } finally { setSubmitting(false); }
  };

  return (
    <div className="bg-[var(--color-cream)] min-h-screen">
      <SEOMeta
        title="Community"
        description="Unisciti alla community di amanti degli animali. Condividi consigli, storie di vita e supporto nel lutto. Articoli esperti su cremazione, urne, ceneri e memoria degli amici a quattro zampe."
        url="/community"
      />
      {/* Hero */}
      <section className="hero-gradient py-12 lg:py-16">
        <div className="container-custom">
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="inline-flex items-center gap-2 text-[var(--color-sage)] text-xs font-bold uppercase tracking-widest mb-3">
              <Heart size={14} /> Community
            </div>
            <h1 className="font-serif text-5xl font-semibold text-[var(--color-deep)] mb-3">{t('community.title')}</h1>
            <p className="text-[var(--color-muted-text)] max-w-xl">{t('community.subtitle')}</p>
          </motion.div>
        </div>
      </section>

      <div className="container-custom py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Feed */}
          <div className="lg:col-span-2">
            {/* Search + New Post */}
            <div className="flex gap-3 mb-6">
              <form onSubmit={handleSearch} className="flex-1 flex gap-2">
                <div className="relative flex-1">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-muted-text)]" />
                  <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Cerca post..." className="pl-9 border-[var(--color-border)] bg-[var(--color-paper)]" />
                </div>
                <Button type="submit" variant="outline" className="border-[var(--color-border)]">Cerca</Button>
              </form>
              {user ? (
                <Button
                  className="bg-[var(--color-sage)] hover:bg-[var(--color-sage-dark)] text-white rounded-xl whitespace-nowrap"
                  onClick={() => setNewPostDialog(true)}
                  data-testid="community-post-create"
                >
                  <Plus size={16} className="mr-2" /> {t('community.new_post')}
                </Button>
              ) : (
                <Button variant="outline" className="border-[var(--color-sage)] text-[var(--color-sage)] whitespace-nowrap" onClick={() => navigate('/login')}>
                  <Plus size={16} className="mr-2" /> Post
                </Button>
              )}
            </div>

            {loading ? (
              <div className="flex justify-center py-16"><div className="spinner" /></div>
            ) : posts.length === 0 ? (
              <div className="text-center py-16">
                <Heart size={40} className="mx-auto text-[var(--color-stone)] mb-4" />
                <p className="text-[var(--color-muted-text)]">{t('community.no_posts')}</p>
              </div>
            ) : (
              <div className="space-y-6">
                {posts.map((p, i) => (
                  <motion.div
                    key={p.id}
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.06, duration: 0.6, ease: [0.22,1,0.36,1] }}
                    className="card-hover bg-[var(--color-paper)] border border-[var(--color-border)] rounded-2xl overflow-hidden shadow-sm group"
                    data-testid={`post-card-${p.id}`}
                  >
                    {p.photo_url && (
                      <div className="h-52 overflow-hidden relative">
                        <img src={p.photo_url} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      </div>
                    )}
                    <div className="p-6">
                      <div className="flex flex-wrap gap-2 mb-3">
                        {p.tags?.map(tag => (
                          <span key={tag} className="text-xs badge-sage px-2 py-0.5 rounded-full cursor-pointer" onClick={() => { setSearch(tag); fetchPosts(); }}>#{tag}</span>
                        ))}
                      </div>
                      <h3 className="font-serif font-semibold text-[var(--color-deep)] text-xl mb-2">{p.title}</h3>
                      <div className="text-sm text-[var(--color-muted-text)] leading-relaxed mb-4">
                        {expandedPost === p.id ? (
                          <PostContent content={p.content} />
                        ) : (
                          <p className="line-clamp-3">{p.content.replace(/\*\*/g, '').split('\n')[0]}</p>
                        )}
                        {p.content?.length > 300 && (
                          <button onClick={() => setExpandedPost(expandedPost === p.id ? null : p.id)} className="text-[var(--color-sage)] text-xs font-medium mt-2 hover:underline">
                            {expandedPost === p.id ? 'Mostra meno ↑' : t('community.read_more') + ' ↓'}
                          </button>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-4 pt-3 border-t border-[var(--color-border)]">
                        <span className="text-xs font-medium text-[var(--color-deep)]">{p.author_name}</span>
                        <span className="text-xs text-[var(--color-muted-text)] ml-auto">{new Date(p.created_at).toLocaleDateString('it-IT')}</span>
                        <button
                          onClick={() => handleLike(p.id)}
                          className="flex items-center gap-1 text-xs text-[var(--color-muted-text)] hover:text-[var(--color-terracotta)] transition-colors"
                          data-testid={`community-post-like-button-${p.id}`}
                        >
                          <ThumbsUp size={14} /> {p.likes}
                        </button>
                        <button
                          onClick={() => setExpandedPost(expandedPost === p.id ? null : p.id)}
                          className="flex items-center gap-1 text-xs text-[var(--color-muted-text)] hover:text-[var(--color-sage)] transition-colors"
                          data-testid={`community-post-comment-button-${p.id}`}
                        >
                          <MessageCircle size={14} /> {p.comments?.length || 0}
                        </button>
                      </div>

                      {/* Comments */}
                      {expandedPost === p.id && (
                        <div className="mt-4 space-y-3">
                          {p.comments?.map(c => (
                            <div key={c.id} className="bg-[var(--color-cream)] rounded-xl p-3">
                              <p className="text-xs font-medium text-[var(--color-deep)]">{c.author_name}</p>
                              <p className="text-sm text-[var(--color-muted-text)] mt-1">{c.content}</p>
                            </div>
                          ))}
                          <div className="flex gap-2">
                            <Input
                              placeholder={t('community.comment_placeholder')}
                              value={commentForms[p.id] || ''}
                              onChange={e => setCommentForms(prev => ({ ...prev, [p.id]: e.target.value }))}
                              className="flex-1 border-[var(--color-border)] text-sm"
                              onKeyDown={e => e.key === 'Enter' && handleComment(p.id)}
                              data-testid={`comment-input-${p.id}`}
                            />
                            <Button size="sm" onClick={() => handleComment(p.id)} className="bg-[var(--color-sage)] text-white">
                              <Send size={14} />
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </motion.div>
                ))}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center gap-2 pt-4">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
                      <button key={n} onClick={() => setPage(n)} className={`w-9 h-9 rounded-xl text-sm ${
                        page === n ? 'bg-[var(--color-sage)] text-white' : 'bg-[var(--color-paper)] border border-[var(--color-border)]'
                      }`}>{n}</button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <aside className="space-y-6">
            <div className="bg-[var(--color-paper)] border border-[var(--color-border)] rounded-2xl p-5">
              <h3 className="font-serif font-semibold text-[var(--color-deep)] mb-4">{t('community.trending_tags')}</h3>
              <div className="flex flex-wrap gap-2">
                {popularTags.map(tag => (
                  <button key={tag} onClick={() => { setSearch(tag); setPage(1); fetchPosts(); }} className="text-xs badge-sage px-3 py-1.5 rounded-full hover:bg-[var(--color-sage)] hover:text-white transition-colors">#{tag}</button>
                ))}
              </div>
            </div>
            <div className="bg-[var(--color-sage-soft)] border border-[var(--color-sage)]/20 rounded-2xl p-5">
              <h3 className="font-serif font-semibold text-[var(--color-deep)] mb-2">{t('community.guidelines')}</h3>
              <p className="text-sm text-[var(--color-muted-text)] leading-relaxed">{t('community.guidelines_text')}</p>
            </div>
            {!user && (
              <div className="bg-[var(--color-terracotta-soft)] border border-[var(--color-terracotta)]/20 rounded-2xl p-5 text-center">
                <Heart size={24} className="mx-auto text-[var(--color-terracotta)] mb-3" />
                <p className="text-sm text-[var(--color-muted-text)] mb-4">{t('community.login_to_post')}</p>
                <Button size="sm" className="bg-[var(--color-sage)] text-white w-full" onClick={() => navigate('/login')}>Accedi</Button>
              </div>
            )}
          </aside>
        </div>
      </div>

      {/* New Post Dialog */}
      <Dialog open={newPostDialog} onOpenChange={setNewPostDialog}>
        <DialogContent className="bg-[var(--color-paper)] border-[var(--color-border)] max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-[var(--color-deep)]">{t('community.new_post')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreatePost} className="space-y-4">
            <div>
              <Label>{t('community.post_title')} *</Label>
              <Input value={postForm.title} onChange={e => setPostForm(f => ({...f, title: e.target.value}))} placeholder="Titolo del tuo post..." className="mt-1 border-[var(--color-border)]" data-testid="post-title-input" />
            </div>
            <div>
              <Label>{t('community.post_content')} *</Label>
              <Textarea value={postForm.content} onChange={e => setPostForm(f => ({...f, content: e.target.value}))} placeholder="Racconta la tua storia..." className="mt-1 border-[var(--color-border)] resize-none" rows={6} data-testid="post-content-input" />
            </div>
            <div>
              <Label><Tag size={12} className="inline mr-1" />{t('community.post_tags')}</Label>
              <Input value={postForm.tags} onChange={e => setPostForm(f => ({...f, tags: e.target.value}))} placeholder="lutto, cane, ricordi" className="mt-1 border-[var(--color-border)]" />
            </div>
            <div>
              <Label>URL immagine (opzionale)</Label>
              <Input value={postForm.photo_url} onChange={e => setPostForm(f => ({...f, photo_url: e.target.value}))} placeholder="https://..." className="mt-1 border-[var(--color-border)]" />
            </div>
            <Button type="submit" disabled={submitting} className="w-full bg-[var(--color-sage)] hover:bg-[var(--color-sage-dark)] text-white rounded-xl" data-testid="post-submit-button">
              {submitting ? 'Pubblicazione...' : t('community.post_submit')}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
